import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Building2, Factory, Info, Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';
import AbbrText from '@/components/AbbrText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ReitCashLensPanel — Round 74 (Feb 11, 2026)
//
// REITs distort the Dividend Triangle because most of their reported
// "earnings" (EPS / Net Income) is reduced by non-cash depreciation on
// the buildings they own. A REIT can have a slightly negative GAAP EPS
// and still generate plenty of cash to cover and grow its dividend —
// PINE is the textbook example (-$2.66M NI but $24M of FFO).
//
// This panel surfaces the REIT-native cash-flow view that income
// investors actually use:
//   FFO (Funds From Operations) = Net Income + Depreciation/Amortization
//   FFO/share                   = FFO ÷ shares outstanding
//   FFO Payout Ratio            = annual dividend ÷ FFO/share
//
// Verdict bands (matches the backend `_payout_verdict`):
//   < 70%   Very Safe
//   70-85%  Sweet Spot
//   85-100% Stretched
//   > 100%  Risky
//
// Renders nothing while loading or for non-REITs (the backend 404s on
// is_reit=false so we never paint an empty card).
const TONE_TO_CLASSES = {
  emerald: { pill: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-200 dark:border-emerald-800',
             card:  'border-emerald-200 dark:border-emerald-900/50' },
  blue:    { pill: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950/60 dark:text-blue-200 dark:border-blue-800',
             card:  'border-blue-200 dark:border-blue-900/50' },
  amber:   { pill: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/60 dark:text-amber-200 dark:border-amber-800',
             card:  'border-amber-200 dark:border-amber-900/50' },
  rose:    { pill: 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/60 dark:text-rose-200 dark:border-rose-800',
             card:  'border-rose-200 dark:border-rose-900/50' },
  muted:   { pill: 'bg-muted text-muted-foreground border-border',
             card:  'border-border' },
};

function fmtMoney(n) {
  if (n === null || n === undefined) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${n < 0 ? '-' : ''}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${n < 0 ? '-' : ''}$${(abs / 1e6).toFixed(2)}M`;
  return `${n < 0 ? '-' : ''}$${abs.toLocaleString()}`;
}

export default function ReitCashLensPanel({ ticker, stock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  // Round P111s (Feb 28, 2026) — Distributable Earnings Toggle.
  // Only used for mortgage REITs. Flips the PRIMARY payout tile +
  // coverage-bar marker between the NAREIT-simple FFO Payout number
  // (agrees with Triangle — default) and the Common Dividend Coverage
  // number (common divs TTM / net income attributable to common
  // shareholders — closest computable proxy for the mREIT-specific
  // "distributable earnings" line item). Persists in localStorage so
  // power users don't need to re-flip on every ticker open.
  const [lensView, setLensView] = useState(() => {
    try {
      const stored = window.localStorage.getItem('reitCashLens:mreitPrimary');
      return stored === 'common' ? 'common' : 'ffo';
    } catch (_e) { return 'ffo'; }
  });
  useEffect(() => {
    try { window.localStorage.setItem('reitCashLens:mreitPrimary', lensView); } catch (_e) { /* localStorage unavailable — silent fallback */ }
  }, [lensView]);

  // Round 92k (Feb 26, 2026) — the backend Cash Lens endpoint now also
  // fires for MLPs (Energy Transfer, EPD, MPLX etc.). MLP unitholders
  // care about Distributable Cash Flow (DCF), not FFO, but the math we
  // use here (NI + D&A) is a reasonable proxy for both because both
  // vehicle types carry heavy non-cash depreciation against GAAP EPS.
  // Only the label changes between the two views.
  const isReitView = !!stock?.is_reit;
  const isMlpView = !isReitView && !!stock?.is_mlp;
  const shouldRender = isReitView || isMlpView;

  useEffect(() => {
    // Don't even hit the endpoint for plain C-corps — we have the
    // vehicle flags in the existing stock payload so we can short-circuit.
    if (!ticker || !shouldRender) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrored(false);
    axios.get(`${API}/stocks/${ticker}/reit-cash-lens`, { withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setErrored(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker, shouldRender]);

  if (!shouldRender) return null;
  if (errored) return null;  // silently hide on backend error — the
                              // existing Stock Metrics still render

  const tone = TONE_TO_CLASSES[data?.verdict_tone || 'muted'];
  const headerLabel = isReitView ? 'REIT Cash Lens' : 'MLP Cash Lens';
  const HeaderIcon = isReitView ? Building2 : Factory;
  const headerIconColor = isReitView
    ? 'text-violet-500 dark:text-violet-400'
    : 'text-amber-500 dark:text-amber-400';
  const cashMetricAbbr = isReitView ? 'FFO' : 'DCF';
  const vehicleAbbr = isReitView ? 'REIT' : 'MLP';
  // Per-share dollar label on the headline stat. REIT investors call
  // this FFO/share; MLP unitholders call it DCF/unit. Keeping the same
  // mathematical denominator (NI + D&A ÷ shares outstanding).
  const perShareLabel = isReitView ? 'FFO' : 'DCF';
  const perShareSuffix = isReitView ? ' / Share' : ' / Unit';

  // Round P111s — Effective payout number, respecting the mREIT toggle.
  // For an mREIT with `lensView === 'common'` we show the Common Div
  // Coverage number on the primary payout tile + coverage-bar marker.
  // For every other case (equity REIT, MLP, or mREIT with FFO view)
  // we keep the original `ffo_payout_ratio_pct`. The verdict tone
  // stays server-driven (`data.verdict_tone`) — the numeric swap is
  // cosmetic; the underlying safety verdict does not change.
  const commonPct = data?.common_dividend_payout_ratio_pct;
  const canToggle = !!data?.is_mortgage_reit && commonPct != null;
  const showingCommon = canToggle && lensView === 'common';
  const primaryPayoutPct = showingCommon ? commonPct : data?.ffo_payout_ratio_pct;
  const primaryPayoutLabel = showingCommon ? 'Common Div' : cashMetricAbbr;
  const primaryPayoutLabelSuffix = showingCommon ? ' Coverage' : ' Payout';

  return (
    <div
      id="reit-cash-lens-anchor"
      className={`rounded-lg border-2 ${tone.card} bg-card p-4 mt-4 scroll-mt-24`}
      data-testid="reit-cash-lens-panel"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <HeaderIcon className={`w-4 h-4 ${headerIconColor}`} />
          <h3 className="text-sm font-bold tracking-wide">
            {headerLabel}
          </h3>
        </div>
        {data?.verdict_label && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${tone.pill}`}
            data-testid="reit-cash-lens-verdict"
          >
            {data.verdict_label}
          </span>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Pulling latest cash-flow statement…
        </div>
      )}

      {!loading && data && (
        <>
          <p className="text-sm font-medium text-foreground mb-3 leading-relaxed">
            {isReitView ? (
              data.is_mortgage_reit ? (
                <>
                  Mortgage <AbbrTip term="REIT">REITs</AbbrTip> (mREITs) don&apos;t own
                  buildings — they hold portfolios of{' '}
                  <AbbrTip term="MBS">MBS</AbbrTip> and mortgage loans and run a
                  levered net-interest-margin carry trade. Their{' '}
                  <AbbrTip term="GAAP">GAAP</AbbrTip>{' '}
                  <AbbrTip term="EPS">EPS</AbbrTip> swings wildly with
                  mark-to-market moves, so the ratio below is a proxy — pair it
                  with the book-value and common-dividend-coverage signals
                  underneath.
                </>
              ) : (
                <>
                  <AbbrTip term="REIT">REITs</AbbrTip> depreciate buildings on paper
                  even when the buildings are generating cash — that drags
                  {' '}<AbbrTip term="GAAP">GAAP</AbbrTip>
                  {' '}<AbbrTip term="EPS">EPS</AbbrTip> below zero for many healthy
                  REITs. The metric below adds depreciation back to show what
                  the dividend actually has to clear.
                </>
              )
            ) : (
              <>
                <AbbrTip term="MLP">MLPs</AbbrTip> depreciate pipelines on paper
                even while they're generating cash — that drags
                {' '}<AbbrTip term="GAAP">GAAP</AbbrTip>
                {' '}<AbbrTip term="EPS">EPS</AbbrTip> and makes the
                payout look stretched. The metric below adds depreciation
                back, approximating the
                {' '}<AbbrTip term="DCF">DCF</AbbrTip> (Distributable Cash Flow)
                that unitholders actually use.
              </>
            )}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            <Stat
              label={<AbbrTip term={cashMetricAbbr}>{perShareLabel}</AbbrTip>}
              labelSuffix={perShareSuffix}
              value={data.ffo_per_share != null ? `$${data.ffo_per_share.toFixed(2)}` : '—'}
              tone="default"
              testid="reit-cash-lens-ffo-per-share"
            />
            <Stat
              label={isReitView ? 'Annual Div' : 'Annual Dist'}
              value={data.annual_dividend != null ? `$${data.annual_dividend.toFixed(2)}` : '—'}
              tone="default"
              testid="reit-cash-lens-div"
            />
            <Stat
              label={showingCommon ? 'Common Div' : <AbbrTip term={cashMetricAbbr}>{cashMetricAbbr}</AbbrTip>}
              labelSuffix={primaryPayoutLabelSuffix}
              value={primaryPayoutPct != null ? `${primaryPayoutPct.toFixed(0)}%` : '—'}
              tone={data.verdict_tone}
              testid="reit-cash-lens-payout"
            />
            <FfoGrowthStat growth={data.ffo_growth_pct} label={cashMetricAbbr} />
            <Stat
              label="Net Income"
              value={fmtMoney(data.net_income)}
              tone={data.net_income != null && data.net_income < 0 ? 'rose' : 'default'}
              testid="reit-cash-lens-ni"
            />
          </div>

          {data.verdict_narrative && (
            <p
              className="text-xs text-foreground/80 leading-relaxed mb-3"
              data-testid="reit-cash-lens-narrative"
            >
              <AbbrText text={data.verdict_narrative} as="span" />
            </p>
          )}

          {/* Round 92o++ — Payout coverage bar. Visualises where the
              current FFO/DCF payout falls across the four verdict
              bands (Very Safe < 70, Sweet Spot 70-85, Stretched 85-100,
              Risky > 100). Converts the abstract "84% payout" number
              into an instantly-readable horizontal gauge. Round P111s
              wires the mREIT Distributable Earnings Toggle so the
              marker follows the primary tile. */}
          {primaryPayoutPct != null && (
            <PayoutCoverageBar
              payoutPct={primaryPayoutPct}
              cashMetric={showingCommon ? 'Common Div' : cashMetricAbbr}
              isMortgageReit={!!data.is_mortgage_reit}
            />
          )}

          {/* Round P111r (Feb 28, 2026) — mREIT-only extras.
              For a mortgage REIT the real safety signal is book-value
              stability + the "distributable earnings" coverage on
              common shareholders, NOT the NAREIT-simple FFO payout.
              We render:
                • a book-value-per-share sparkline (up to 5y) so users
                  can see whether the leveraged carry trade is eroding
                  principal to fund the payout;
                • a supplementary Common Dividend Coverage tile with
                  the closest we can compute to "distributable
                  earnings" (common divs ÷ net income attributable to
                  common shareholders). */}
          {data.is_mortgage_reit && (
            <MReitExtras
              ticker={ticker}
              bookValue={data.book_value_per_share}
              bookValueHistory={data.book_value_history}
              commonDivPayoutPct={data.common_dividend_payout_ratio_pct}
              niacsPerShare={data.niacs_per_share}
              lensView={lensView}
              onLensViewChange={setLensView}
              canToggle={canToggle}
            />
          )}

          {/* Round 92o++ — FFO/DCF per-share inline sparkline. 3-5
              years of historical (Net Income + D&A) ÷ shares
              outstanding, plotted as a lightweight inline SVG with
              year-end labels on the first and last points. Income
              investors care less about Q-to-Q noise and more about
              whether the per-share cushion above the dividend is
              widening or shrinking over multiple years. Round 92o+++
              adds the dividend-per-year overlay so users can see the
              cushion as a visible gap (FFO line minus Div line). */}
          <FfoSparkline
            history={data.ffo_history}
            cashMetric={cashMetricAbbr}
            unitSuffix={perShareSuffix}
          />

          <p
            className="text-sm font-medium text-foreground leading-relaxed flex items-start gap-1.5"
            data-testid="reit-cash-lens-disclaimer"
          >
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {isReitView ? (
                data.is_mortgage_reit ? (
                  <>
                    <AbbrTip term="FFO">FFO</AbbrTip> here = Net Income + Depreciation/Amortization
                    (NAREIT-simple).{' '}
                    {data.data_source === 'massive' && (
                      <>Pulled from <span className="font-bold">SEC TTM filings via Massive</span> (period ending{' '}
                      {data.period_end || 'latest'}).{' '}</>
                    )}
                    For a mortgage REIT this is a directional proxy — mREITs publish their own{' '}
                    <span className="italic">distributable earnings</span> (a.k.a. core earnings) in
                    supplemental filings that exclude MBS mark-to-market swings and add back
                    non-cash loan-loss provisions. Watch the book-value trend + common-dividend
                    coverage above alongside this ratio, not this ratio alone.
                  </>
                ) : (
                  <>
                    <AbbrTip term="FFO">FFO</AbbrTip> = Net Income + Depreciation/Amortization
                    (NAREIT standard).{' '}
                    {data.data_source === 'massive' && (
                      <>Pulled from <span className="font-bold">SEC TTM filings via Massive</span> (period ending{' '}
                      {data.period_end || 'latest'}) — audit-grade.{' '}</>
                    )}
                    Actual {' '}<AbbrTip term="AFFO">AFFO</AbbrTip> from management's supplemental
                    filing is typically 5-15% lower than FFO. Use this as a
                    directional gut-check, not as audited financials.
                  </>
                )
              ) : (
                <>
                  Cash proxy = Net Income + Depreciation/Amortization, a
                  reasonable approximation of
                  {' '}<AbbrTip term="DCF">DCF</AbbrTip>.{' '}
                  {data.data_source === 'massive' && (
                    <>Pulled from <span className="font-bold">SEC TTM filings via Massive</span> (period ending{' '}
                    {data.period_end || 'latest'}).{' '}</>
                  )}
                  Audited Distributable Cash Flow lives in management's quarterly supplemental
                  and is usually 0-10% lower than this estimate. Use as a
                  directional gut-check, not audited financials.
                </>
              )}
              {' '}Updated{' '}
              {data.fetched_at ? new Date(data.fetched_at).toLocaleDateString() : ''}.
            </span>
          </p>
        </>
      )}
    </div>
  );
}

function FfoGrowthStat({ growth, label = 'FFO' }) {
  // Dedicated stat for cash-metric YoY growth with a directional icon +
  // tone mapping. Positive growth = the cushion above the dividend is
  // widening (compounder); negative = shrinking (deteriorating).
  // `label` is "FFO" for REITs, "DCF" for MLPs — the underlying math
  // (NI + D&A delta vs prior year) is identical for both vehicles.
  let tone = 'default';
  let Icon = Minus;
  let value = '—';
  if (growth !== null && growth !== undefined) {
    value = `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`;
    if (growth >= 5) { tone = 'emerald'; Icon = TrendingUp; }
    else if (growth >= 0) { tone = 'blue'; Icon = TrendingUp; }
    else if (growth >= -5) { tone = 'amber'; Icon = TrendingDown; }
    else { tone = 'rose'; Icon = TrendingDown; }
  }
  const colors = {
    default: 'text-foreground',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    blue:    'text-blue-600 dark:text-blue-400',
    amber:   'text-amber-600 dark:text-amber-400',
    rose:    'text-rose-600 dark:text-rose-400',
  };
  return (
    <div
      className="rounded-md border bg-background/40 px-2.5 py-2 text-center"
      data-testid="reit-cash-lens-ffo-growth"
    >
      <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-0.5">
        <AbbrTip term={label}>{label}</AbbrTip> <AbbrTip term="YOY">YoY</AbbrTip>
      </p>
      <p className={`text-base font-bold font-mono ${colors[tone]} flex items-center justify-center gap-1`}>
        <Icon className="w-3.5 h-3.5" />
        {value}
      </p>
    </div>
  );
}

function Stat({ label, labelSuffix, value, tone = 'default', testid }) {
  const colors = {
    default: 'text-foreground',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    blue:    'text-blue-600 dark:text-blue-400',
    amber:   'text-amber-600 dark:text-amber-400',
    rose:    'text-rose-600 dark:text-rose-400',
    muted:   'text-muted-foreground',
  };
  return (
    <div
      className="rounded-md border bg-background/40 px-2.5 py-2 text-center"
      data-testid={testid}
    >
      <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-0.5">
        {label}{labelSuffix}
      </p>
      <p className={`text-base font-bold font-mono ${colors[tone] || colors.default}`}>
        {value}
      </p>
    </div>
  );
}


function PayoutCoverageBar({ payoutPct, cashMetric = 'FFO', isMortgageReit = false }) {
  // Horizontal gauge mapping the current FFO/DCF payout ratio onto the
  // verdict bands used by `_payout_verdict` in the backend.
  //
  // Two band variants (Round P111r):
  //   Equity REIT / MLP:
  //     < 70%   Very Safe  · 70-85%  Sweet · 85-100% Stretched · > 100%  Risky
  //   Mortgage REIT (mREIT):
  //     < 85%   Very Safe  · 85-100% Sweet · 100-110% Stretched · > 110% Risky
  // The scale tops out at 130% so a 265% reading (Realty Income on a
  // GAAP-EPS basis would dwarf the bar) still renders meaningfully —
  // we clamp the marker and add a "+X% over" tooltip via title.
  if (payoutPct == null || !Number.isFinite(payoutPct)) return null;
  const MAX = isMortgageReit ? 140 : 130;
  const clamped = Math.min(payoutPct, MAX);
  const markerLeft = (clamped / MAX) * 100;
  // Band widths as percentages of the MAX-scale.
  const bands = isMortgageReit
    ? [
        { key: 'safe',    widthPct: (85 / MAX) * 100,           colorClass: 'bg-emerald-400/40 dark:bg-emerald-500/30' },
        { key: 'sweet',   widthPct: ((100 - 85) / MAX) * 100,   colorClass: 'bg-blue-400/40 dark:bg-blue-500/30' },
        { key: 'stretch', widthPct: ((110 - 100) / MAX) * 100,  colorClass: 'bg-amber-400/40 dark:bg-amber-500/30' },
        { key: 'risky',   widthPct: ((MAX - 110) / MAX) * 100,  colorClass: 'bg-rose-400/40 dark:bg-rose-500/30' },
      ]
    : [
        { key: 'safe',    widthPct: (70 / MAX) * 100,           colorClass: 'bg-emerald-400/40 dark:bg-emerald-500/30' },
        { key: 'sweet',   widthPct: ((85 - 70) / MAX) * 100,    colorClass: 'bg-blue-400/40 dark:bg-blue-500/30' },
        { key: 'stretch', widthPct: ((100 - 85) / MAX) * 100,   colorClass: 'bg-amber-400/40 dark:bg-amber-500/30' },
        { key: 'risky',   widthPct: ((MAX - 100) / MAX) * 100,  colorClass: 'bg-rose-400/40 dark:bg-rose-500/30' },
      ];
  // Marker color matches the verdict tone for visual consistency.
  const stretchCutoff = isMortgageReit ? 100 : 85;
  const riskyCutoff = isMortgageReit ? 110 : 100;
  const safeCutoff = isMortgageReit ? 85 : 70;
  let markerColor = 'bg-emerald-600 dark:bg-emerald-400';
  if (payoutPct > riskyCutoff)       markerColor = 'bg-rose-600 dark:bg-rose-400';
  else if (payoutPct >= stretchCutoff)  markerColor = 'bg-amber-600 dark:bg-amber-400';
  else if (payoutPct >= safeCutoff)  markerColor = 'bg-blue-600 dark:bg-blue-400';
  const overshot = payoutPct > MAX;
  return (
    <div className="mb-3" data-testid="reit-cash-lens-coverage-bar">
      <div className="flex items-baseline justify-between mb-1 gap-2">
        <div className="flex items-center gap-1">
          <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            {cashMetric} payout coverage
          </p>
          {/* Round P111s — mREIT band-explainer info tip.
              Explains why the Sweet Spot band sits at 85-100% for
              mortgage REITs (they distribute nearly all distributable
              earnings by design) vs 70-85% for equity REITs (they
              retain more for growth capex + acquisitions). */}
          {isMortgageReit && (
            <button
              type="button"
              tabIndex={0}
              data-testid="reit-cash-lens-band-tip"
              className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-muted text-muted-foreground hover:bg-foreground hover:text-background transition-colors cursor-help"
              title={
                "Why 85 / 100 / 110 for mREITs?\n\n" +
                "Mortgage REITs are levered net-interest-margin carry trades. " +
                "They must distribute at least 90% of taxable income to keep REIT status, " +
                "and in practice they routinely pay out 90-100% of distributable earnings " +
                "because they don't reinvest in physical assets like equity REITs do.\n\n" +
                "So we widen the Sweet Spot to 85-100% (vs 70-85% for equity REITs) and " +
                "push Stretched / Risky out to 100-110% / >110%. A 97% payout that would " +
                "read Stretched for an equity REIT is right at the mREIT-sector norm."
              }
              aria-label="Why these bands for mortgage REITs?"
            >
              <span className="text-[9px] font-bold leading-none">?</span>
            </button>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground/80 font-mono">
          {payoutPct.toFixed(0)}% of {cashMetric}
          {overshot && <span className="text-rose-600 dark:text-rose-400 ml-1">· off chart</span>}
        </p>
      </div>
      <div
        className="relative h-2.5 w-full flex rounded-full overflow-hidden border border-border/60"
        title={overshot ? `${payoutPct.toFixed(0)}% of ${cashMetric} — off chart (gauge tops out at ${MAX}%).` : `${payoutPct.toFixed(0)}% of ${cashMetric}.`}
      >
        {bands.map((b) => (
          <div key={b.key} className={b.colorClass} style={{ width: `${b.widthPct}%` }} />
        ))}
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-0.5 h-4 ${markerColor} rounded-full shadow-sm`}
          style={{ left: `calc(${markerLeft}% - 1px)` }}
          aria-label="Current payout marker"
        />
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-muted-foreground/70 font-mono">
        <span>0%</span>
        <span className="text-emerald-600 dark:text-emerald-400">{isMortgageReit ? '85' : '70'} Safe</span>
        <span className="text-blue-600 dark:text-blue-400">{isMortgageReit ? '100' : '85'} Sweet</span>
        <span className="text-amber-600 dark:text-amber-400">{isMortgageReit ? '110' : '100'} Stretch</span>
        <span>{MAX}%+</span>
      </div>
    </div>
  );
}


// Round P111r (Feb 28, 2026) — mREIT-only supplementary block.
//
// A leveraged carry trade (mortgage REIT) can look "healthy" on FFO
// payout even when it's slowly eating its own book value to fund the
// dividend. The two elements below give users the second-order safety
// signals that mREIT investors actually track on quarterly calls:
//
//   1. Book Value / Share sparkline — 5-year annual series from
//      Massive balance sheets. A steady or rising line is the
//      strongest single indicator the distribution is sustainable
//      through rate cycles. A steady grind lower is a slow-motion
//      dividend cut in progress.
//   2. Common Dividend Coverage tile — common dividends TTM /
//      net income attributable to common shareholders. This is the
//      cleanest proxy we can compute for the "distributable
//      earnings" number mREITs publish in supplementals — it excludes
//      preferred-dividend leakage and reflects the actual economics
//      of the levered book.
function MReitExtras({ ticker, bookValue, bookValueHistory, commonDivPayoutPct, niacsPerShare, lensView, onLensViewChange, canToggle }) {
  const hasHistory = Array.isArray(bookValueHistory) && bookValueHistory.length >= 2;
  const hasCommon = commonDivPayoutPct != null && Number.isFinite(commonDivPayoutPct);
  if (!bookValue && !hasHistory && !hasCommon) return null;

  // Common Dividend Coverage verdict — mirrors the mREIT verdict bands
  // used elsewhere but with a plainer palette for a secondary tile.
  let commonTone = 'muted';
  if (hasCommon) {
    if (commonDivPayoutPct < 85) commonTone = 'emerald';
    else if (commonDivPayoutPct < 100) commonTone = 'blue';
    else if (commonDivPayoutPct <= 110) commonTone = 'amber';
    else commonTone = 'rose';
  }
  const toneClasses = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    blue:    'text-blue-600 dark:text-blue-400',
    amber:   'text-amber-600 dark:text-amber-400',
    rose:    'text-rose-600 dark:text-rose-400',
    muted:   'text-muted-foreground',
  };

  // Book-value trend direction — first vs last point in the sparkline.
  let bvTrend = null;
  if (hasHistory) {
    const first = bookValueHistory[0]?.book_value_per_share;
    const last = bookValueHistory[bookValueHistory.length - 1]?.book_value_per_share;
    if (first && last) {
      const pct = ((last - first) / first) * 100;
      bvTrend = { pct, dir: pct >= 0 ? 'up' : 'down' };
    }
  }

  return (
    <div className="mb-3 rounded-md border border-border/60 bg-background/30 p-3" data-testid="reit-cash-lens-mreit-extras">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          mREIT safety signals
        </p>
        {/* Round P111s — Distributable Earnings Toggle. Small pill
            switch that flips the primary payout number displayed in
            the tile grid + coverage bar between "FFO Payout" (Triangle
            number, default) and "Common Div Coverage" (distributable
            proxy). Only rendered when both metrics are available. */}
        {canToggle && (
          <div
            className="inline-flex items-center rounded-full border border-border bg-background/60 p-0.5 text-[10px] font-semibold"
            data-testid="reit-cash-lens-lens-toggle"
            role="tablist"
            aria-label="Cash Lens primary payout metric"
          >
            <button
              type="button"
              role="tab"
              aria-selected={lensView === 'ffo'}
              data-testid="reit-cash-lens-toggle-ffo"
              onClick={() => onLensViewChange?.('ffo')}
              className={`px-2.5 py-1 rounded-full transition-colors ${lensView === 'ffo' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
            >
              FFO Payout
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={lensView === 'common'}
              data-testid="reit-cash-lens-toggle-common"
              onClick={() => onLensViewChange?.('common')}
              className={`px-2.5 py-1 rounded-full transition-colors ${lensView === 'common' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
              title="Common dividends TTM ÷ net income attributable to common shareholders — the closest computable proxy for the distributable-earnings ratio mREITs publish in supplemental filings."
            >
              Common Div Coverage
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start">
        {/* Book-value sparkline (takes 2 columns on desktop for readability) */}
        {(hasHistory || bookValue != null) && (
          <div className="sm:col-span-2" data-testid="reit-cash-lens-bv-sparkline-wrap">
            <div className="flex items-baseline justify-between mb-1">
              <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Book value / share
              </p>
              <p className="text-[10px] font-mono text-muted-foreground/80">
                {bookValue != null ? `$${Number(bookValue).toFixed(2)}` : '—'}
                {bvTrend && (
                  <span className={`ml-1.5 ${bvTrend.dir === 'up' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {bvTrend.dir === 'up' ? '▲' : '▼'} {Math.abs(bvTrend.pct).toFixed(1)}% since {bookValueHistory[0]?.year}
                  </span>
                )}
              </p>
            </div>
            {hasHistory ? (
              <BookValueSparkline history={bookValueHistory} />
            ) : (
              <p className="text-[10px] text-muted-foreground/70 italic">
                Historical series unavailable — need 2+ annual balance-sheet filings.
              </p>
            )}
          </div>
        )}
        {/* Common dividend coverage tile */}
        {hasCommon && (
          <div
            className="rounded-md border bg-background/60 px-2.5 py-2 text-center"
            data-testid="reit-cash-lens-common-div-payout"
            title={`Common dividends TTM ÷ net income attributable to common shareholders${niacsPerShare != null ? ` (NIACS: $${Number(niacsPerShare).toFixed(2)}/share)` : ''}. Closest computable proxy for the 'distributable earnings' payout mREITs report in supplemental filings.`}
          >
            <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-0.5">
              Common div coverage
            </p>
            <p className={`text-base font-bold font-mono ${toneClasses[commonTone]}`}>
              {commonDivPayoutPct.toFixed(0)}%
            </p>
            <p className="text-[9px] text-muted-foreground/70 mt-0.5">
              of net income attributable to common
            </p>
          </div>
        )}
      </div>

      {/* Round P111s — mREIT power-user tools:
            • Sector pin: one-click watchlist creation for Agency /
              Commercial / Hybrid mREIT buckets so users can compare
              book-value trends across the sector.
            • Book-value alert: fires when this ticker's latest annual
              BVPS drops by N% year-over-year. Real dividend-cut
              early-warning signal for a leveraged carry trade. */}
      <MReitToolbar ticker={ticker} />
    </div>
  );
}


// Round P111s (Feb 28, 2026) — Toolbar rendered inside MReitExtras.
// Encapsulates its own async state so the parent Cash Lens re-renders
// stay cheap. Emits `watchlist-changed` after a successful pin so the
// AppHeader star count and WatchlistSelector both refresh in-place.
function MReitToolbar({ ticker }) {
  const [pinning, setPinning] = useState(null);       // sector key while POST is in-flight
  const [pinResult, setPinResult] = useState(null);   // last successful pin result
  const [pinError, setPinError] = useState(null);
  const [alertPct, setAlertPct] = useState(5);
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertResult, setAlertResult] = useState(null);
  const [alertError, setAlertError] = useState(null);

  const SECTORS = [
    { key: 'agency',     label: 'Agency',     name: 'mREIT · Agency',     title: 'Agency + residential mREITs (AGNC, NLY, ARR, DX, TWO, ORC, IVR, CIM, EARN, MFA)' },
    { key: 'commercial', label: 'Commercial', name: 'mREIT · Commercial', title: 'Commercial mortgage REITs (STWD, BXMT, ABR, KREF, LADR, ARI, RC, TRTX, GPMT, ACRE, NREF, SACH)' },
    { key: 'hybrid',     label: 'Hybrid',     name: 'mREIT · Hybrid',     title: 'Hybrid / originator mREITs (RITM, HASI, PMT, EFC, RWT, ADAM)' },
  ];

  async function pinSector(key, name) {
    setPinning(key); setPinError(null); setPinResult(null);
    try {
      const secRes = await axios.get(`${API}/mreit-sectors`, { withCredentials: true });
      const sector = (secRes.data?.sectors || []).find((s) => s.key === key);
      if (!sector) throw new Error('sector not found');
      const res = await axios.post(
        `${API}/watchlists/bulk-create`,
        { name, tickers: sector.tickers },
        { withCredentials: true },
      );
      setPinResult({ key, added: res.data?.added || 0, name });
      // Fire the store-level event so AppHeader count + WatchlistSelector rehydrate.
      try { window.dispatchEvent(new CustomEvent('watchlist-changed')); } catch (_e) { /* event unsupported — silent */ }
    } catch (e) {
      setPinError(e?.response?.data?.detail || e?.message || 'Failed to pin sector');
    } finally {
      setPinning(null);
    }
  }

  async function saveBvAlert() {
    if (!ticker) return;
    const pct = Number(alertPct);
    if (!Number.isFinite(pct) || pct <= 0) {
      setAlertError('Enter a positive percent'); return;
    }
    setAlertSaving(true); setAlertError(null); setAlertResult(null);
    try {
      await axios.post(
        `${API}/alerts`,
        {
          ticker,
          alert_type: 'book_value_drop_pct',
          threshold: pct,
          note: `Alert if ${ticker} BVPS drops ${pct}%+ YoY`,
        },
        { withCredentials: true },
      );
      setAlertResult({ pct });
    } catch (e) {
      setAlertError(e?.response?.data?.detail || e?.message || 'Failed to save alert');
    } finally {
      setAlertSaving(false);
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-border/60 space-y-2" data-testid="reit-cash-lens-mreit-toolbar">
      {/* Sector pin row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          Pin sector watchlist
        </span>
        {SECTORS.map((s) => (
          <button
            key={s.key}
            type="button"
            data-testid={`reit-cash-lens-pin-${s.key}`}
            onClick={() => pinSector(s.key, s.name)}
            disabled={pinning === s.key}
            title={s.title}
            className="px-2.5 py-1 text-[10px] font-semibold rounded-full border border-border bg-background/60 hover:bg-foreground hover:text-background disabled:opacity-60 transition-colors"
          >
            {pinning === s.key ? '…' : `+ ${s.label}`}
          </button>
        ))}
      </div>
      {pinResult && (
        <p className="text-[10px] text-emerald-600 dark:text-emerald-400" data-testid="reit-cash-lens-pin-success">
          Added {pinResult.added} tickers to &ldquo;{pinResult.name}&rdquo; — open Watchlists to view.
        </p>
      )}
      {pinError && (
        <p className="text-[10px] text-rose-600 dark:text-rose-400">{pinError}</p>
      )}

      {/* Book value alert row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          Alert me if BVPS drops
        </span>
        <div className="inline-flex items-center rounded-md border border-border bg-background/60 overflow-hidden">
          <input
            type="number"
            min="1"
            max="90"
            step="1"
            value={alertPct}
            onChange={(e) => setAlertPct(e.target.value)}
            className="w-14 px-2 py-1 text-[11px] font-mono bg-transparent focus:outline-none"
            data-testid="reit-cash-lens-bv-alert-input"
          />
          <span className="pr-2 text-[10px] text-muted-foreground">%+ YoY</span>
        </div>
        <button
          type="button"
          onClick={saveBvAlert}
          disabled={alertSaving || !ticker}
          data-testid="reit-cash-lens-bv-alert-save"
          className="px-2.5 py-1 text-[10px] font-semibold rounded-md border border-border bg-background/60 hover:bg-foreground hover:text-background disabled:opacity-60 transition-colors"
        >
          {alertSaving ? 'Saving…' : 'Set alert'}
        </button>
      </div>
      {alertResult && (
        <p className="text-[10px] text-emerald-600 dark:text-emerald-400" data-testid="reit-cash-lens-bv-alert-success">
          Alert saved — {ticker} will trigger when annual BVPS drops {alertResult.pct}% or more YoY.
        </p>
      )}
      {alertError && (
        <p className="text-[10px] text-rose-600 dark:text-rose-400">{alertError}</p>
      )}
    </div>
  );
}


// Book value / share sparkline (mREIT-only).
// Lightweight inline SVG — 5 annual points max, one line, first &
// last year labelled below the axis. Fills the width of its
// container so it matches the wider FFO sparkline visually.
function BookValueSparkline({ history }) {
  if (!Array.isArray(history) || history.length < 2) return null;
  const values = history.map((p) => Number(p.book_value_per_share));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 0.0001);
  const W = 320;
  const H = 42;
  const PAD_X = 4;
  const step = history.length > 1 ? (W - PAD_X * 2) / (history.length - 1) : 0;
  const y = (v) => H - 4 - ((v - min) / range) * (H - 12);
  const pts = values.map((v, i) => `${PAD_X + i * step},${y(v).toFixed(1)}`).join(' ');
  const first = values[0];
  const last = values[values.length - 1];
  const trending = last >= first ? 'stroke-emerald-500' : 'stroke-rose-500';
  const fillClass = last >= first ? 'fill-emerald-500/10' : 'fill-rose-500/10';
  const areaPts = `${PAD_X},${H} ${pts} ${PAD_X + (values.length - 1) * step},${H}`;
  return (
    <div data-testid="reit-cash-lens-bv-sparkline">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-10" preserveAspectRatio="none">
        <polygon points={areaPts} className={fillClass} />
        <polyline
          points={pts}
          fill="none"
          strokeWidth="1.5"
          className={trending}
        />
        {values.map((v, i) => (
          <circle
            key={i}
            cx={PAD_X + i * step}
            cy={y(v)}
            r={i === 0 || i === values.length - 1 ? 2.5 : 1.5}
            className={i === 0 || i === values.length - 1 ? trending.replace('stroke-', 'fill-') : 'fill-muted-foreground/40'}
          />
        ))}
      </svg>
      <div className="flex justify-between text-[9px] text-muted-foreground/70 font-mono mt-0.5">
        <span>{history[0]?.year} · ${first?.toFixed(2)}</span>
        <span>{history[history.length - 1]?.year} · ${last?.toFixed(2)}</span>
      </div>
    </div>
  );
}


function FfoSparkline({ history, cashMetric = 'FFO', unitSuffix = ' / Share' }) {
  // Lightweight SVG sparkline of historical per-share FFO/DCF, with an
  // optional dividend-per-year overlay so the FFO − Div cushion is
  // visible as a gap that widens or narrows year over year. The
  // overlay toggle is ON by default; clicking the chip hides the
  // dividend line for users who want a cleaner FFO-only view.
  const [showDiv, setShowDiv] = useState(true);
  // Round 92o+++++ — Tap-to-pin year tooltip. Click any year label or
  // the per-year hit-zone on the chart to pin a detail card showing
  // FFO, Div, Payout %, Cushion % for that year. Click outside or the
  // × button to unpin. Mobile-friendly because the SVG <title> tooltip
  // requires hover, which doesn't exist on touch devices.
  const [pinnedYear, setPinnedYear] = useState(null);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!pinnedYear) return undefined;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setPinnedYear(null);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [pinnedYear]);

  if (!Array.isArray(history) || history.length < 2) return null;
  // Filter out partial-data points so the line doesn't dive to zero on
  // years without a recorded payment (defensive — yfinance data rarely
  // misses, but new IPOs can have a partial year).
  const divPoints = history.filter((h) => h.div_per_share != null && h.div_per_share > 0);
  const hasDivData = divPoints.length >= 2;
  const ffoValues = history.map((h) => h.ffo_per_share);
  const divValues = hasDivData ? divPoints.map((h) => h.div_per_share) : [];
  // Shared min/max so both lines plot on the same y-axis. Without this
  // a tight dividend line at the bottom + a tall FFO line at the top
  // would auto-scale to different ranges and the "cushion" gap would
  // visually disappear.
  const combined = showDiv && hasDivData ? [...ffoValues, ...divValues] : ffoValues;
  const min = Math.min(...combined);
  const max = Math.max(...combined);
  const range = max - min || 1;
  const W = 240;
  const H = 44;
  const PAD_X = 4;
  const PAD_Y = 4;
  const stepX = (W - PAD_X * 2) / (history.length - 1);
  const buildPoints = (rows, key) => rows.map((h) => {
    const idx = history.findIndex((x) => x.year === h.year);
    const x = PAD_X + idx * stepX;
    const y = PAD_Y + (H - PAD_Y * 2) * (1 - (h[key] - min) / range);
    return { x, y, year: h.year, val: h[key] };
  });
  const ffoPoints = buildPoints(history, 'ffo_per_share');
  const ffoLinePath = ffoPoints.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(' ');
  const ffoAreaPath = `${ffoLinePath} L${ffoPoints[ffoPoints.length - 1].x},${H - PAD_Y} L${ffoPoints[0].x},${H - PAD_Y} Z`;
  // Trend tone — last vs first FFO dictates color.
  const firstVal = ffoPoints[0].val;
  const lastVal = ffoPoints[ffoPoints.length - 1].val;
  const trendUp = lastVal >= firstVal;
  const stroke = trendUp ? '#10b981' : '#f43f5e';  // emerald-500 / rose-500
  const fill = trendUp ? 'rgba(16, 185, 129, 0.18)' : 'rgba(244, 63, 94, 0.16)';
  // Dividend line — amber, dashed. Render only when toggled on AND we
  // have ≥ 2 dividend points to draw.
  let divLinePath = null;
  let divPointsPlot = [];
  if (showDiv && hasDivData) {
    divPointsPlot = buildPoints(divPoints, 'div_per_share');
    divLinePath = divPointsPlot.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(' ');
  }

  // ── Smart label placement (Round 92o++++) ──────────────────────────
  // When the dividend line hugs the bottom of the chart, the axis row
  // (year labels + legend + cushion callout) printed BELOW the SVG
  // visually collides with the dashed line. Detect that case and flip
  // the row to ABOVE the chart instead. We use the avg of all div
  // points (more stable than just the last point) and a hysteresis
  // threshold of 0.55·H so the layout doesn't flicker when the line
  // crosses the middle.
  const avgDivY = divPointsPlot.length > 0
    ? divPointsPlot.reduce((s, p) => s + p.y, 0) / divPointsPlot.length
    : null;
  const axisRowOnTop = avgDivY !== null && avgDivY > H * 0.55;

  // ── Cushion summary (Round 92o++++) ────────────────────────────────
  // One-line plain-English summary of FFO − Dividend cushion. We
  // compute the latest year's cushion as a % of FFO ("9% buffer") and
  // the YoY delta in percentage points ("widening +2pp"). Tone tracks
  // the YoY delta so the eye lands on improvement vs deterioration.
  let cushion = null;
  if (hasDivData) {
    const yearMap = new Map(divPoints.map((d) => [d.year, d.div_per_share]));
    const enriched = history
      .filter((h) => yearMap.has(h.year))
      .map((h) => ({
        year: h.year,
        ffo: h.ffo_per_share,
        div: yearMap.get(h.year),
        pct: h.ffo_per_share > 0
          ? ((h.ffo_per_share - yearMap.get(h.year)) / h.ffo_per_share) * 100
          : null,
      }))
      .filter((r) => r.pct !== null);
    if (enriched.length >= 2) {
      const last = enriched[enriched.length - 1];
      const prev = enriched[enriched.length - 2];
      const deltaPp = last.pct - prev.pct;
      let cushionTone = 'text-muted-foreground';
      let direction = 'flat YoY';
      if (deltaPp > 0.5) { cushionTone = 'text-emerald-600 dark:text-emerald-400'; direction = `widening +${deltaPp.toFixed(1)}pp YoY`; }
      else if (deltaPp < -0.5) { cushionTone = 'text-rose-600 dark:text-rose-400'; direction = `narrowing ${deltaPp.toFixed(1)}pp YoY`; }
      cushion = {
        pct: last.pct,
        tone: cushionTone,
        direction,
      };
    }
  }

  // The axis row reused in both above-chart and below-chart layouts.
  // text-[10px] (bumped from 9px per user request) so the row stays
  // readable when the lines crowd it. Year labels at each end are now
  // clickable buttons that pin a detail card for that year.
  const renderYearLabel = (year) => (
    <button
      type="button"
      onClick={() => setPinnedYear((p) => (p === year ? null : year))}
      className={`font-mono px-0.5 rounded hover:bg-muted/60 hover:text-foreground transition-colors ${
        pinnedYear === year ? 'bg-muted text-foreground font-semibold' : ''
      }`}
      title={`Show ${year} details`}
      data-testid={`reit-cash-lens-year-${year}`}
      aria-pressed={pinnedYear === year}
    >
      {year}
    </button>
  );
  const axisRow = (
    <div className={`flex justify-between text-[10px] text-muted-foreground/80 font-mono ${axisRowOnTop ? 'mb-0.5' : 'mt-0.5'}`}>
      {renderYearLabel(ffoPoints[0].year)}
      {/* Inline legend + cushion stat. Legend only renders when the
          dividend line is drawn; cushion only when we have ≥ 2 years
          of overlap data. */}
      {showDiv && hasDivData && (
        <span className="flex items-center gap-2 flex-wrap justify-center">
          <span className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-0.5 rounded-full" style={{ backgroundColor: stroke }} />
            {cashMetric}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2.5 border-t border-dashed border-amber-500" />
            Div
          </span>
          {cushion && (
            <span className={`font-semibold ${cushion.tone}`} title={`Latest cushion = ${cushion.pct.toFixed(1)}% of ${cashMetric}; ${cushion.direction}.`}>
              · cushion {cushion.pct.toFixed(0)}% {cushion.direction}
            </span>
          )}
        </span>
      )}
      {renderYearLabel(ffoPoints[ffoPoints.length - 1].year)}
    </div>
  );

  // ── Pinned year detail card (Round 92o+++++) ───────────────────────
  // When a user taps a year label or chart point, surface a small
  // overlay card with that year's exact figures. We anchor the card
  // horizontally to the data point and prefer the OPPOSITE side of
  // the chart from where the lines concentrate (mirrors the same
  // logic as the axis row to avoid drawing the card over the lines).
  let pinnedCard = null;
  if (pinnedYear) {
    const row = history.find((h) => h.year === pinnedYear);
    if (row) {
      const ffoVal = row.ffo_per_share;
      const divVal = row.div_per_share;
      const hasDiv = divVal != null && divVal > 0;
      const payoutPct = hasDiv && ffoVal > 0 ? (divVal / ffoVal) * 100 : null;
      const cushionPct = hasDiv && ffoVal > 0 ? ((ffoVal - divVal) / ffoVal) * 100 : null;
      // Tone for the payout pill
      let payoutTone = 'text-muted-foreground';
      if (payoutPct != null) {
        if (payoutPct < 70) payoutTone = 'text-emerald-600 dark:text-emerald-400';
        else if (payoutPct < 85) payoutTone = 'text-blue-600 dark:text-blue-400';
        else if (payoutPct <= 100) payoutTone = 'text-amber-600 dark:text-amber-400';
        else payoutTone = 'text-rose-600 dark:text-rose-400';
      }
      pinnedCard = (
        <div
          className="rounded-md border bg-popover shadow-md p-2 text-[10px] font-mono"
          data-testid="reit-cash-lens-pinned-card"
          role="dialog"
          aria-label={`${pinnedYear} cash lens details`}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-[10px] font-bold tracking-wide text-foreground">{pinnedYear}</span>
            <button
              type="button"
              onClick={() => setPinnedYear(null)}
              className="text-muted-foreground hover:text-foreground text-[11px] leading-none px-1"
              title="Close"
              data-testid="reit-cash-lens-pinned-close"
              aria-label="Close pinned year details"
            >
              ×
            </button>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span title={`${cashMetric} per share`}>
              <span className="text-muted-foreground">{cashMetric}</span>{' '}
              <span className="font-semibold text-foreground">${ffoVal != null ? ffoVal.toFixed(2) : '—'}</span>
            </span>
            <span title="Annual dividend per share">
              <span className="text-muted-foreground">Div</span>{' '}
              <span className="font-semibold text-foreground">${hasDiv ? divVal.toFixed(2) : '—'}</span>
            </span>
            <span title="Dividend as % of cash metric">
              <span className="text-muted-foreground">Payout</span>{' '}
              <span className={`font-semibold ${payoutTone}`}>{payoutPct != null ? `${payoutPct.toFixed(0)}%` : '—'}</span>
            </span>
            <span title="Cash retained above the dividend">
              <span className="text-muted-foreground">Cushion</span>{' '}
              <span className="font-semibold text-foreground">{cushionPct != null ? `${cushionPct.toFixed(0)}%` : '—'}</span>
            </span>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="mb-3 relative" data-testid="reit-cash-lens-sparkline" ref={wrapRef}>
      <div className="flex items-baseline justify-between mb-1 gap-2">
        <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          {cashMetric}{unitSuffix} · {history.length}yr trend
        </p>
        <div className="flex items-center gap-1.5">
          {hasDivData && (
            <button
              type="button"
              onClick={() => setShowDiv((v) => !v)}
              className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border transition-colors ${
                showDiv
                  ? 'border-amber-300/60 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300'
                  : 'border-border bg-muted/40 text-muted-foreground line-through opacity-70 hover:opacity-90'
              }`}
              title={showDiv ? 'Hide dividend overlay' : 'Show dividend overlay'}
              data-testid="reit-cash-lens-toggle-div"
              aria-pressed={showDiv}
            >
              Dividend
            </button>
          )}
          <p className="text-[10px] text-muted-foreground/80 font-mono">
            ${firstVal.toFixed(2)} → ${lastVal.toFixed(2)}
          </p>
        </div>
      </div>
      {axisRowOnTop && axisRow}
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-11 cursor-pointer select-none"
          preserveAspectRatio="none"
          role="img"
          aria-label={`${cashMetric} per share over ${history.length} years${showDiv && hasDivData ? ', with dividend overlay' : ''}. Click a year to pin details.`}
        >
          <path d={ffoAreaPath} fill={fill} />
          <path d={ffoLinePath} stroke={stroke} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          {ffoPoints.map((p, i) => (
            <circle key={`f${i}`} cx={p.x} cy={p.y} r={i === ffoPoints.length - 1 ? 2.4 : 1.4} fill={stroke}>
              <title>{p.year}: {cashMetric} ${p.val.toFixed(2)}</title>
            </circle>
          ))}
          {divLinePath && (
            <>
              <path d={divLinePath} stroke="#f59e0b" strokeWidth="1.3" fill="none" strokeDasharray="3 2" strokeLinecap="round" strokeLinejoin="round" />
              {divPointsPlot.map((p, i) => (
                <circle key={`d${i}`} cx={p.x} cy={p.y} r={i === divPointsPlot.length - 1 ? 2 : 1.2} fill="#f59e0b">
                  <title>{p.year}: Div ${p.val.toFixed(2)}</title>
                </circle>
              ))}
            </>
          )}
          {/* Per-year invisible hit zones. Each spans half of stepX on
              either side of its data point and the full chart height,
              so users can tap anywhere in that vertical strip — much
              easier than hitting a 2.4px-radius circle, especially on
              touch screens. */}
          {ffoPoints.map((p, i) => {
            const zoneW = stepX;
            const zoneX = Math.max(0, p.x - zoneW / 2);
            return (
              <rect
                key={`z${i}`}
                x={zoneX}
                y={0}
                width={zoneW}
                height={H}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onClick={() => setPinnedYear((prev) => (prev === p.year ? null : p.year))}
                data-testid={`reit-cash-lens-hitzone-${p.year}`}
              >
                <title>Tap to pin {p.year} details</title>
              </rect>
            );
          })}
        </svg>
      </div>
      {!axisRowOnTop && axisRow}
      {pinnedCard && (
        <div className={`absolute left-0 right-0 ${axisRowOnTop ? 'top-full mt-1' : 'bottom-full mb-1'} z-20 pointer-events-auto`}>
          {pinnedCard}
        </div>
      )}
    </div>
  );
}
